import React, { useEffect, useState } from "react";
import { Modal, Form, Button, InputGroup, Spinner, Alert } from "react-bootstrap";
import * as familiaGastosApi from "../../api/familiaGastos";
import { formatoNumero } from "../../utils/moneda";

const ORIGENES = [
  { value: "Sueldo", label: "Sueldo de...", ayuda: "El sueldo que declaró un miembro. Con \"Total\", la suma del sueldo de todos." },
  { value: "Neto", label: "Neto de...", ayuda: "Sueldo menos lo que ese miembro aportó a los gastos. Con \"Total\", la suma del neto de todos." },
  { value: "TotalGastos", label: "Total Gastos de...", ayuda: "Todo lo que un miembro aportó, sumando todos los gastos. Con \"Total\", la suma de todos los miembros." },
  { value: "AporteItem", label: "Aporte de un gasto de...", ayuda: "Lo que un miembro puso de verdad en los gastos que marques abajo. Con \"Total\", lo que pusieron entre todos." },
  { value: "MontoPrincipal", label: "Monto presupuestado de...", ayuda: "El monto (columna Monto) que TÚ cargaste para los gastos que marques abajo — no depende de quién aportó, es el presupuesto de la familia." },
  { value: "TarjetaCredito", label: "Tarjeta de crédito...", ayuda: "El Monto Usado de la tarjeta que elijas, menos sus Gastos Fijos ya pagados de este mes." },
  { value: "CobroFamilia", label: "Cobros Familia de...", ayuda: "Lo que te falta cobrarle a ese deudor en Cobros Familia (o a todos, con \"Total\"). Se actualiza solo: si amortizan baja, si cancelan queda en $0, si vuelve a haber un cobro pendiente vuelve a sumar." },
  { value: "Ajuste", label: "Otra fila de ajuste...", ayuda: "El valor ya calculado de otra fila de Ajuste creada antes que esta (para encadenar)." },
];

function nuevoIngrediente() {
  return { tipoOrigen: "Neto", usuarioId: null, gastoComunId: null, ajusteOrigenId: null, cuentaId: null, deudorId: null, signo: 1 };
}

/** Alta/edición de una fila de "Ajuste": un monto manual, o una fórmula que suma/resta
 * el Sueldo/Neto/Total Gastos de un miembro, o el valor ya calculado de una fila de ajuste
 * anterior (encadenar). Al editar, solo se pueden referenciar filas creadas antes que esta. */
export default function ModalAjusteFamiliar({ show, onHide, ajuste, miembros, gastosComunes, tarjetas = [], deudores = [], ajustesDisponibles, onGuardado }) {
  const [descripcion, setDescripcion] = useState("");
  const [tipo, setTipo] = useState("Manual");
  const [montoManual, setMontoManual] = useState("");
  const [ingredientes, setIngredientes] = useState([nuevoIngrediente()]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  // Los gastos con monto $0 no aportan nada a una suma y solo ensucian la lista.
  const gastosConMonto = gastosComunes.filter((g) => Number(g.montoPrincipal) !== 0);
  const todosLosGastoIds = () => gastosConMonto.map((g) => g.gastoComunId);

  const aportePorGasto = (usuarioId, gastoComunId) => {
    const miembro = miembros.find((m) => String(m.usuarioId) === String(usuarioId));
    return Number(miembro?.aportes.find((a) => a.gastoComunId === gastoComunId)?.monto || 0);
  };

  // En "Aporte de un gasto", lo relevante no es el presupuesto sino cuánto puso ESE miembro: si un
  // miembro no aportó nada a un gasto, ese gasto no tiene nada que aportar a la suma para él.
  const gastosVisibles = (ing) => {
    if (ing.tipoOrigen !== "AporteItem") return gastosConMonto;
    if (ing.usuarioId && ing.usuarioId !== "TOTAL") {
      return gastosComunes.filter((g) => aportePorGasto(ing.usuarioId, g.gastoComunId) !== 0);
    }
    return gastosComunes.filter((g) => miembros.some((m) => aportePorGasto(m.usuarioId, g.gastoComunId) !== 0));
  };

  useEffect(() => {
    if (!show) return;
    if (ajuste) {
      setDescripcion(ajuste.descripcion);
      setTipo(ajuste.tipo);
      setMontoManual(ajuste.montoManual ?? "");
      setIngredientes(ajuste.ingredientes?.length ? ajuste.ingredientes.map((i) => {
        const conUsuario = { ...i, usuarioId: i.usuarioId == null && i.tipoOrigen !== "Ajuste" && i.tipoOrigen !== "MontoPrincipal" ? "TOTAL" : i.usuarioId };
        if (i.tipoOrigen === "MontoPrincipal" || i.tipoOrigen === "AporteItem") {
          const ids = i.gastoComunIds?.length ? i.gastoComunIds : i.gastoComunId != null ? [i.gastoComunId] : todosLosGastoIds();
          return { ...conUsuario, gastoComunIds: ids };
        }
        return conUsuario;
      }) : [nuevoIngrediente()]);
    } else {
      setDescripcion("");
      setTipo("Manual");
      setMontoManual("");
      setIngredientes([nuevoIngrediente()]);
    }
    setError("");
  }, [show, ajuste]);

  // Al editar una fila existente, una fórmula solo puede referenciar filas creadas ANTES (AjusteId menor)
  // para que nunca haya ciclos; al crear una nueva, cualquier fila ya existente sirve.
  const ajustesReferenciables = ajustesDisponibles.filter((a) => !ajuste || a.ajusteId < ajuste.ajusteId);

  const actualizarIngrediente = (idx, cambios) =>
    setIngredientes((prev) => prev.map((ing, i) => (i === idx ? { ...ing, ...cambios } : ing)));

  const quitarIngrediente = (idx) => setIngredientes((prev) => prev.filter((_, i) => i !== idx));

  const handleGuardar = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    try {
      const payload = {
        ajusteId: ajuste?.ajusteId ?? null,
        descripcion,
        tipo,
        montoManual: tipo === "Manual" ? Number(montoManual || 0) : null,
        ingredientes: tipo === "Formula" ? ingredientes.map((ing) => ({
          tipoOrigen: ing.tipoOrigen,
          usuarioId: ing.tipoOrigen === "Ajuste" || ing.tipoOrigen === "MontoPrincipal" || ing.tipoOrigen === "TarjetaCredito" || ing.usuarioId === "TOTAL" || !ing.usuarioId ? null : Number(ing.usuarioId),
          gastoComunId: null,
          gastoComunIds: ing.tipoOrigen === "MontoPrincipal" || ing.tipoOrigen === "AporteItem" ? (ing.gastoComunIds || []).map(Number) : null,
          ajusteOrigenId: ing.tipoOrigen === "Ajuste" ? Number(ing.ajusteOrigenId) : null,
          cuentaId: ing.tipoOrigen === "TarjetaCredito" ? Number(ing.cuentaId) : null,
          deudorId: ing.tipoOrigen === "CobroFamilia" && ing.deudorId ? Number(ing.deudorId) : null,
          signo: Number(ing.signo),
        })) : null,
        orden: ajuste?.orden ?? 0,
      };
      await familiaGastosApi.guardarAjuste(payload);
      onGuardado();
    } catch (err) {
      setError(err?.response?.data?.mensaje || "No se pudo guardar el ajuste.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={handleGuardar}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.1rem" }}>{ajuste ? "Editar ajuste" : "Nuevo ajuste"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="py-2">{error}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label className="small mb-1">Descripción</Form.Label>
            <Form.Control value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required placeholder="Ej. Will &amp; Kev, Jorge Recibe..." />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small mb-1 d-block">Tipo</Form.Label>
            <Form.Check inline type="radio" label="Monto manual" name="tipoAjuste" checked={tipo === "Manual"} onChange={() => setTipo("Manual")} />
            <Form.Check inline type="radio" label="Fórmula" name="tipoAjuste" checked={tipo === "Formula"} onChange={() => setTipo("Formula")} />
          </Form.Group>

          {tipo === "Manual" ? (
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Monto</Form.Label>
              <InputGroup style={{ maxWidth: 200 }}>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control type="number" step="0.01" value={montoManual} onChange={(e) => setMontoManual(e.target.value)} required />
              </InputGroup>
            </Form.Group>
          ) : (
            <div>
              <Form.Label className="small mb-1 d-block">Ingredientes (se suman o restan)</Form.Label>
              {ingredientes.map((ing, idx) => (
                <div key={idx} className="d-flex flex-wrap gap-2 align-items-center mb-2 p-2 bg-body-secondary" style={{ borderRadius: 6 }}>
                  <Form.Select size="sm" style={{ width: 80 }} value={ing.signo} onChange={(e) => actualizarIngrediente(idx, { signo: Number(e.target.value) })}>
                    <option value={1}>+ Suma</option>
                    <option value={-1}>− Resta</option>
                  </Form.Select>
                  <Form.Select size="sm" style={{ width: 170 }} value={ing.tipoOrigen} onChange={(e) => actualizarIngrediente(idx, {
                    tipoOrigen: e.target.value, usuarioId: null, gastoComunId: null, ajusteOrigenId: null, cuentaId: null, deudorId: null,
                    gastoComunIds: e.target.value === "MontoPrincipal" ? todosLosGastoIds() : e.target.value === "AporteItem" ? [] : null,
                  })}>
                    {ORIGENES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Form.Select>
                  {ing.tipoOrigen === "Ajuste" ? (
                    <Form.Select size="sm" style={{ width: 200 }} value={ing.ajusteOrigenId || ""} onChange={(e) => actualizarIngrediente(idx, { ajusteOrigenId: e.target.value })} required>
                      <option value="">Selecciona la fila...</option>
                      {ajustesReferenciables.map((a) => <option key={a.ajusteId} value={a.ajusteId}>{a.descripcion} (${formatoNumero(a.valor)})</option>)}
                    </Form.Select>
                  ) : ing.tipoOrigen === "TarjetaCredito" ? (
                    <Form.Select size="sm" style={{ width: 200 }} value={ing.cuentaId || ""} onChange={(e) => actualizarIngrediente(idx, { cuentaId: e.target.value })} required>
                      <option value="">Selecciona la tarjeta...</option>
                      {tarjetas.map((t) => <option key={t.cuentaId} value={t.cuentaId}>{t.alias || t.banco} (${formatoNumero(t.saldoActual)})</option>)}
                    </Form.Select>
                  ) : ing.tipoOrigen === "CobroFamilia" ? (
                    <Form.Select size="sm" style={{ width: 200 }} value={ing.deudorId || ""} onChange={(e) => actualizarIngrediente(idx, { deudorId: e.target.value })}>
                      <option value="">Total (todos los deudores)</option>
                      {deudores.map((d) => <option key={d.deudorId} value={d.deudorId}>{d.nombre}</option>)}
                    </Form.Select>
                  ) : ing.tipoOrigen === "MontoPrincipal" ? null : (
                    <Form.Select size="sm" style={{ width: 200 }} value={ing.usuarioId || ""} onChange={(e) => {
                      const nuevoUsuarioId = e.target.value;
                      if (ing.tipoOrigen === "AporteItem") {
                        const visibles = gastosVisibles({ ...ing, usuarioId: nuevoUsuarioId });
                        actualizarIngrediente(idx, { usuarioId: nuevoUsuarioId, gastoComunIds: visibles.map((g) => g.gastoComunId) });
                      } else {
                        actualizarIngrediente(idx, { usuarioId: nuevoUsuarioId });
                      }
                    }} required>
                      <option value="">Selecciona el miembro...</option>
                      <option value="TOTAL">Total (todos los miembros)</option>
                      {miembros.map((m) => <option key={m.usuarioId} value={m.usuarioId}>{m.nombres}</option>)}
                    </Form.Select>
                  )}
                  {(ing.tipoOrigen === "AporteItem" || ing.tipoOrigen === "MontoPrincipal") && (
                    <div
                      className="p-2 bg-body"
                      style={{
                        width: "100%", maxHeight: 110, overflowY: "auto", border: "1px solid var(--bs-border-color)", borderRadius: 4,
                        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", columnGap: 8, rowGap: 2,
                      }}
                    >
                      {gastosVisibles(ing).map((g) => (
                        <Form.Check
                          key={g.gastoComunId}
                          className="text-truncate"
                          style={{ fontSize: "0.8rem" }}
                          type="checkbox"
                          label={g.nombre}
                          checked={(ing.gastoComunIds || []).includes(g.gastoComunId)}
                          onChange={(e) => {
                            const actuales = ing.gastoComunIds || [];
                            const nuevos = e.target.checked
                              ? [...actuales, g.gastoComunId]
                              : actuales.filter((id) => id !== g.gastoComunId);
                            actualizarIngrediente(idx, { gastoComunIds: nuevos });
                          }}
                        />
                      ))}
                      {!gastosVisibles(ing).length && (
                        <p className="text-muted small mb-0">
                          {ing.tipoOrigen === "AporteItem" ? "Este miembro no tiene aportes en ningún gasto." : "No hay gastos con monto."}
                        </p>
                      )}
                    </div>
                  )}
                  <p className="text-muted small mb-0 w-100">{ORIGENES.find((o) => o.value === ing.tipoOrigen)?.ayuda}</p>
                  {ingredientes.length > 1 && (
                    <Button size="sm" variant="outline-danger" onClick={() => quitarIngrediente(idx)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  )}
                </div>
              ))}
              <Button size="sm" variant="outline-secondary" onClick={() => setIngredientes((prev) => [...prev, nuevoIngrediente()])}>
                <i className="bi bi-plus-lg me-1"></i>Agregar ingrediente
              </Button>
              {ajustesReferenciables.length === 0 && (
                <p className="text-muted small mt-2 mb-0">Aún no hay otras filas de ajuste creadas para encadenar.</p>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>Cancelar</Button>
          <Button variant="primary" type="submit" disabled={guardando}>
            {guardando ? <Spinner size="sm" animation="border" /> : "Guardar"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
